import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT, useDateFmt } from '../lang.js';
import Icon from '../Icon.jsx';
import PageSkeleton from '../Skeleton.jsx';

// Etiketler sözlükten: `ly.kind.<k>` ve `ly.tx.<k>`.
const KINDS = ['DISCOUNT_FIXED', 'DISCOUNT_PERCENT', 'FREE_ITEM'];

/**
 * Sadakat programı + ödüller + müşteri arama.
 *
 * Panelde bilinçli olarak GÖSTERİLEN iki şey:
 *  · `ledgerBalance` eksi olabilir ("borçlu puan") ve GİZLENMEZ — iade sonrası
 *    oluşur, bir sonraki kazanç kapatır (docs/LOYALTY.md §4).
 *  · Puan yazılmama sebebi: "neden puan gelmedi" en sık soru.
 */
export default function Loyalty() {
  const t = useT();
  const fmt = useDateFmt();
  const [program, setProgram] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [gated, setGated] = useState(false);

  const [form, setForm] = useState({ kind: 'DISCOUNT_FIXED', maxPerOrder: 1, channel: 'BOTH' });
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [detail, setDetail] = useState(null);

  async function load() {
    setErr('');
    try {
      const [p, r] = await Promise.all([api('/api/loyalty/program'), api('/api/loyalty/rewards')]);
      setProgram(p.program);
      setRewards(r.rewards);
      try {
        const m = await api('/api/menu');
        setItems((m.categories || []).flatMap((c) => c.items || []));
      } catch { /* menü okunamazsa bedava ürün seçimi devre dışı kalır */ }
    } catch (e) {
      if (e.status === 402) { setGated(true); return; }
      setErr(errMessage(t, e));
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function act(fn, ok) {
    setErr('');
    setNote('');
    setBusy(true);
    try {
      await fn();
      if (ok) setNote(ok);
      await load();
    } catch (e) {
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  if (gated) {
    return (
      <div className="card">
        <b>{t('ly.gatedTitle')}</b>
        <p className="muted">{t('ly.gatedNote')}</p>
      </div>
    );
  }
  if (!program) return <PageSkeleton shape="form" />;

  const num = (k) => (e) => setProgram({ ...program, [k]: e.target.value === '' ? '' : Number(e.target.value) });

  return (
    <>
      {err && <p className="err">{err}</p>}
      {note && <p className="muted">{note}</p>}

      <div className="card">
        <div className="row">
          <b>{t('ly.program')}</b>
          <label className="muted" style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={!!program.enabled}
              onChange={(e) => setProgram({ ...program, enabled: e.target.checked })} />
            {t('ly.enabled')}
          </label>
        </div>

        <div className="row" style={{ marginTop: 10, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <label>{t('ly.pointsPerUnit')}</label>
            <input type="number" min="0" style={{ maxWidth: 110 }}
              value={program.pointsPerCurrencyUnit} onChange={num('pointsPerCurrencyUnit')} />
          </div>
          <div>
            <label>{t('ly.orderBonus')}</label>
            <input type="number" min="0" style={{ maxWidth: 110 }}
              value={program.pointsPerOrder} onChange={num('pointsPerOrder')} />
          </div>
          <div>
            <label>{t('ly.minOrder')}</label>
            <input type="number" min="0" style={{ maxWidth: 130 }}
              value={program.minOrderMinor} onChange={num('minOrderMinor')} />
          </div>
          <div>
            <label>{t('ly.expiryDays')}</label>
            <input type="number" min="1" style={{ maxWidth: 150 }}
              value={program.pointExpiryDays ?? ''}
              onChange={(e) => setProgram({ ...program, pointExpiryDays: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
          <div>
            <label>{t('ly.welcomePoints')}</label>
            <input type="number" min="0" style={{ maxWidth: 110 }}
              value={program.welcomePoints} onChange={num('welcomePoints')} />
          </div>
          <div>
            <label>{t('ly.birthdayPoints')}</label>
            <input type="number" min="0" style={{ maxWidth: 110 }}
              value={program.birthdayPoints} onChange={num('birthdayPoints')} />
          </div>
        </div>

        <label className="muted" style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={!!program.combinableWithDiscounts}
            onChange={(e) => setProgram({ ...program, combinableWithDiscounts: e.target.checked })} />
          {t('ly.combinable')}
        </label>

        <button className="primary" style={{ marginTop: 14 }} disabled={busy}
          onClick={() => act(() => api('/api/loyalty/program', { method: 'PUT', body: program }), t('ly.programSaved'))}>
          <Icon n="save" />{t('save')}
        </button>

        <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>{t('ly.earnNote')}</p>
      </div>

      <div className="card">
        <b>{t('ly.rewards')}</b>
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr><th>{t('name')}</th><th>{t('prn.kind')}</th><th>{t('value')}</th><th>{t('ly.threshold')}</th><th>{t('ly.perOrder')}</th><th></th></tr>
          </thead>
          <tbody>
            {rewards.length === 0 && <tr><td colSpan="6" className="muted">{t('ly.noRewards')}</td></tr>}
            {rewards.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td className="muted">{t(`ly.kind.${r.kind}`)}</td>
                <td className="muted">
                  {r.kind === 'DISCOUNT_FIXED' && `${(r.amountMinor / 100).toFixed(2)}`}
                  {r.kind === 'DISCOUNT_PERCENT' && `%${(r.percentBps / 100).toFixed(2)}`}
                  {r.kind === 'FREE_ITEM' && t('ly.cheapestEligible')}
                </td>
                <td>{r.thresholdPoints}</td>
                <td className="muted">{r.maxPerOrder}</td>
                <td>
                  <button disabled={busy}
                    onClick={() => act(() => api(`/api/loyalty/rewards/${r.id}`, { method: 'PATCH', body: { enabled: !r.enabled } }))}>
                    <Icon n="power" />{r.enabled ? t('staff.disable') : t('staff.enable')}
                  </button>
                  <button disabled={busy} style={{ marginLeft: 6 }}
                    onClick={() => act(() => api(`/api/loyalty/rewards/${r.id}`, { method: 'DELETE' }), t('ly.rewardArchived'))}>
                    <Icon n="trash" />{t('remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="row" style={{ marginTop: 14, flexWrap: 'wrap' }}>
          <input style={{ maxWidth: 170 }} placeholder={t('ly.rewardName')}
            value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select style={{ maxWidth: 160 }} value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`ly.kind.${k}`)}</option>)}
          </select>

          {form.kind === 'DISCOUNT_FIXED' && (
            <input style={{ maxWidth: 130 }} type="number" min="1" placeholder={t('ly.amountCents')}
              value={form.amountMinor ?? ''} onChange={(e) => setForm({ ...form, amountMinor: Number(e.target.value) })} />
          )}
          {form.kind === 'DISCOUNT_PERCENT' && (
            <input style={{ maxWidth: 130 }} type="number" min="1" max="10000" placeholder={t('ly.percentBps')}
              value={form.percentBps ?? ''} onChange={(e) => setForm({ ...form, percentBps: Number(e.target.value) })} />
          )}
          {form.kind === 'FREE_ITEM' && (
            <select style={{ maxWidth: 200 }} value={(form.eligibleItemIds || [])[0] || ''}
              onChange={(e) => setForm({ ...form, eligibleItemIds: e.target.value ? [Number(e.target.value)] : [] })}>
              <option value="">{t('ly.pickItem')}</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          )}

          <input style={{ maxWidth: 110 }} type="number" min="1" placeholder={t('ly.thresholdPoints')}
            value={form.thresholdPoints ?? ''} onChange={(e) => setForm({ ...form, thresholdPoints: Number(e.target.value) })} />
          <input style={{ maxWidth: 110 }} type="number" min="1" placeholder={t('ly.perOrder')}
            value={form.maxPerOrder} onChange={(e) => setForm({ ...form, maxPerOrder: Number(e.target.value) })} />

          <button className="primary" disabled={busy} onClick={() => act(async () => {
            await api('/api/loyalty/rewards', { method: 'POST', body: form });
            setForm({ kind: 'DISCOUNT_FIXED', maxPerOrder: 1, channel: 'BOTH' });
          }, t('ly.rewardAdded'))}><Icon n="plus" />{t('add')}</button>
        </div>
      </div>

      <div className="card">
        <b>{t('ly.findCustomer')}</b>
        <p className="muted" style={{ marginTop: 4 }}>{t('ly.findNote')}</p>
        <div className="row">
          <input style={{ maxWidth: 240 }} placeholder={t('ly.searchPlaceholder')}
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button disabled={busy || q.trim().length < 3} onClick={() => act(async () => {
            const r = await api(`/api/loyalty/customers?q=${encodeURIComponent(q.trim())}`);
            setFound(r.customers);
            setDetail(null);
          })}><Icon n="search" />{t('search')}</button>
        </div>

        {found && found.length === 0 && <p className="muted">{t('ly.noMatch')}</p>}
        {found && found.length > 0 && (
          <table style={{ marginTop: 10 }}>
            <thead><tr><th>{t('email')}</th><th>{t('name')}</th><th>{t('ly.points')}</th><th>{t('ly.verified')}</th><th></th></tr></thead>
            <tbody>
              {found.map((c) => (
                <tr key={c.customerId}>
                  <td>{c.email}</td>
                  <td className="muted">{c.name || '—'}</td>
                  <td>{c.pointsBalance}</td>
                  <td className="muted">{c.emailVerified ? t('ly.yes') : t('ly.no')}</td>
                  <td>
                    <button onClick={() => act(async () => {
                      const d = await api(`/api/loyalty/customers/${c.customerId}`);
                      setDetail(d);
                    })}><Icon n="eye" />{t('detail')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {detail && (
          <div style={{ marginTop: 16 }}>
            <div className="row">
              <b>{detail.customer.email}</b>
              <span style={{ marginLeft: 'auto' }}>{t('ly.pointsOf', { n: detail.pointsBalance })}</span>
            </div>
            {detail.ledgerBalance < 0 && (
              <p className="err" style={{ marginTop: 6 }}>
                {t('ly.negativeBalance', { n: detail.ledgerBalance })}
              </p>
            )}
            <table style={{ marginTop: 10 }}>
              <thead><tr><th>{t('bill.date')}</th><th>{t('prn.kind')}</th><th>{t('ly.points')}</th><th>{t('ly.note')}</th></tr></thead>
              <tbody>
                {detail.transactions.map((x) => (
                  <tr key={x.id}>
                    <td className="muted">{fmt.dateTime(x.createdAt)}</td>
                    <td className="muted">{t(`ly.tx.${x.kind}`)}</td>
                    <td style={{ color: x.points < 0 ? '#c00' : undefined }}>{x.points > 0 ? `+${x.points}` : x.points}</td>
                    <td className="muted">{x.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="row" style={{ marginTop: 12 }}>
              <input style={{ maxWidth: 110 }} type="number" placeholder={t('ly.adjPoints')}
                value={detail.adjPoints ?? ''} onChange={(e) => setDetail({ ...detail, adjPoints: Number(e.target.value) })} />
              <input style={{ maxWidth: 220 }} placeholder={t('ly.adjReason')}
                value={detail.adjNote ?? ''} onChange={(e) => setDetail({ ...detail, adjNote: e.target.value })} />
              <button disabled={busy || !detail.adjPoints || !detail.adjNote} onClick={() => act(async () => {
                await api(`/api/loyalty/customers/${detail.customer.id}/adjust`, {
                  method: 'POST', body: { points: detail.adjPoints, note: detail.adjNote },
                });
                const d = await api(`/api/loyalty/customers/${detail.customer.id}`);
                setDetail(d);
              }, t('ly.adjWritten'))}><Icon n="pencil" />{t('ly.adjust')}</button>
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>{t('ly.adjNote')}</p>
          </div>
        )}
      </div>
    </>
  );
}
